import {
    isPlayerAction,
    isRemovePlayerEvent,
    PlayerId,
    Projection,
    SharedGameState,
    useGameStore,
} from '@glark-newco/game-library';
import {shallow} from 'zustand/shallow';
import {
    isAssignPlayerNodePayload,
    isSaveObjectivePayload,
    isTransferRingEvent,
    RingId,
} from '../SupplyChainPayloadTypes';

export type PlayerRingsMap = Record<PlayerId, RingId[]>;

export interface PlayerRingsSlice extends SharedGameState {
    playerRings?: PlayerRingsMap;
}

export const playerRingsProjection: Projection<PlayerRingsSlice> = {
    name: 'playerRingsProjection',
    project: (state, newEvents) => {
        const rings = {...(state.playerRings ?? {})};
        let modified = false;

        newEvents.forEach(ev => {
            if (isPlayerAction(ev) && isAssignPlayerNodePayload(ev.payload)) {
                const assign = ev.payload;
                rings[assign.playerId] = assign.rings.map(r => r.ringId);
                modified = true;
            } else if (isPlayerAction(ev) && isTransferRingEvent(ev.payload)) {
                const tr = ev.payload;
                // Remove from source
                if (rings[tr.sourcePlayerId]) {
                    rings[tr.sourcePlayerId] = rings[tr.sourcePlayerId].filter(id => id !== tr.ringId);
                }
                // Add to recipient
                rings[tr.recipientPlayerId] = [
                    ...(rings[tr.recipientPlayerId] ?? []),
                    tr.ringId,
                ];
                modified = true;
            } else if (isPlayerAction(ev) && isSaveObjectivePayload(ev.payload)) {
                const save = ev.payload;
                if (rings[save.playerId]) {
                    rings[save.playerId] = rings[save.playerId].filter(id => id !== save.ringId);
                    modified = true;
                }
            } else if (isRemovePlayerEvent(ev)) {
                const removedId = ev.removePlayerId;
                const replacementId = ev.replacementId;
                if (removedId && rings[removedId]) {
                    if (replacementId) {
                        const transferred = rings[removedId];
                        rings[replacementId] = [
                            ...(rings[replacementId] ?? []),
                            ...transferred,
                        ];
                    }
                    delete rings[removedId];
                    modified = true;
                }
            }
        });

        return modified ? {playerRings: rings} : {};
    },
    reset: () => ({playerRings: {}}),
};

export const usePlayerRings:(playerId: PlayerId) => RingId[] = (playerId: PlayerId): RingId[] => {
    return useGameStore(
        (s: PlayerRingsSlice) => s.playerRings?.[playerId] ?? [],
        shallow,
    );
};
